import { defineStore } from 'pinia';
import simpleRingtoneMono from '@/assets/ringtone/Simple ringtone mono.mp3';
import { readonly, ref } from 'vue';
import type { Ringtone } from '@/data/Ringtone';
import { BrowserStorageKey } from '@/constants/browserStorage';
import { parseDataFromBrowserStorage, saveToBrowserStorage } from '@/utils/storage';

export const useAudioStore = defineStore('audio', () => {
  const DEFAULT_RINGTONE: Ringtone = createRingtone(simpleRingtoneMono, simpleRingtoneMono);

  const __externalRingtone = ref(getRingtone(BrowserStorageKey.EXTERNAL_RINGTONE));
  const __internalRingtone = ref(getRingtone(BrowserStorageKey.INTERNAL_RINGTONE));

  // todo ringtones should be persisted in localStorage but kept as variable so we don't have to call functions each time to get curretn value
  const setSelectedExternalRingtone = (ringtone: Ringtone) => {
    __externalRingtone.value = ringtone;
    saveToBrowserStorage(BrowserStorageKey.EXTERNAL_RINGTONE, ringtone);
  };

  const getSelectedExternalRingtone = readonly(__externalRingtone);

  const setSelectedInternalRingtone = (ringtone: Ringtone) => {
    __internalRingtone.value = ringtone;
    saveToBrowserStorage(BrowserStorageKey.INTERNAL_RINGTONE, ringtone);
  };

  const getSelectedInternalRingtone = readonly(__internalRingtone);

  function getRingtone(localStorageKeyName: string): Ringtone {
    const ringtone: Ringtone | null = parseDataFromBrowserStorage<Ringtone>(localStorageKeyName);
    if (!ringtone) return DEFAULT_RINGTONE;
    return createRingtone(ringtone.displayName, ringtone.path);
  }

  function createRingtone(displayName: string, path: string) {
    // Regex to get the proper name of ringtone from file path, "ringtone" is optional since path after build is /assets/*.mp3, and on localhost the path is /assets/ringtone/*.mp3
    const regex = /assets\/(ringtone\/)?([^/]+)\.mp3$/;
    const matches = displayName.match(regex);
    const adjustedDisplayName = matches ? decodeURIComponent(matches[2]!) : displayName;

    return {
      // Use split to remove hash part that is added to ringtone name after build
      displayName: adjustedDisplayName.split('-')[0]!,
      path: path,
    };
  }

  return {
    getSelectedExternalRingtone,
    setSelectedExternalRingtone,
    getSelectedInternalRingtone,
    setSelectedInternalRingtone,
    createRingtone,
  };
});
